/**
 * ProgressNoteForm – Medplum-backed note editor.
 *
 * Uses `useQuestionnaireForm` for form state management (same runtime as
 * Template Library preview) and saves the QuestionnaireResponse to Medplum.
 *
 * Template selection fetches active note templates from the Template Library
 * (Medplum Questionnaires in Appointments and Forms categories).
 */
import type {
  Appointment,
  ChargeItem,
  Encounter,
  EpisodeOfCare,
  Patient,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  RelatedPerson,
} from '@medplum/fhirtypes';
import type { QuestionnaireFormState } from '@medplum/react-hooks';
import { getReferenceString, createReference } from '@medplum/core';
import { useMedplum, useMedplumProfile, useQuestionnaireForm } from '@medplum/react-hooks';
import {
  Alert,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Select,
  Stack,
  Stepper,
  Text,
  Title,
} from '@mantine/core';
import {
  CohortProgressNoteTitleExtras,
  type CohortNoteKind,
} from './components/CohortProgressNoteTitleExtras';
import { practitionerPreferredReference } from '../../lib/loadPractitionerPreferredLabels';
import { config } from '@/config';
import { notify } from '@/lib/notify';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { QuestionnaireTextUndoProvider } from '@/components/questionnaire/QuestionnaireTextUndoContext';
import { useNoteTemplates, type NoteTemplateOption } from './hooks';
import { isMockQuestionnaireResponseId } from './mockAppointmentNoteData';
import { getMockAppointmentNoteTemplates } from './mockAppointmentTemplates';
import type { DocumentComment } from '../supervision/types';
import {
  PreviewItemArray,
} from '../settings/template-library/components/TemplatePreviewDrawer';
import {
  APPOINTMENT_TAG_SYSTEM,
  appointmentTag,
  DETAILED_STATUS_EXT_URL,
  getDetailedStatus,
  getReviewCommentsFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
  REVIEW_COMMENTS_EXT_URL,
  setReviewCommentsOnQR,
  SUPERVISOR_SIGNATURE_EXT_URL,
  mergeQuestionnaireResponseExtensionsForSave,
  getAiNoteCheckResultsFromQR,
  type AiNoteCheckResult,
} from './fhirNoteMapping';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import { validateParticipantAttendance } from '@/schemas/participant-attendance.schema';
import {
  isCalendarClientAttendanceResolved,
  normalizeCalendarClientAttendanceStatus,
} from '@/lib/calendarClientAttendanceStatus';
import {
  expandGroupNoteQuestionnaire,
  getAppointmentGroupRef,
  isCohortGroupAppointment,
  isGroupNotePatientSectionPrototype,
} from '@/lib/groupTherapy';
import { careTeamAccountRefsFromEpisode } from '@unify/fhir-model/resources/episode-of-care';
import {
  enrichBillingCodesWithCpt,
  resolveNoteMetadataForEditor,
  setNoteMetadataOnQR,
} from './noteMetadataMapping';
import { syncNoteMetadataEditsToSources } from './utils/syncNoteMetadataToSources';
import {
  isSignedNoteLockError,
  persistNoteFormAttendance,
} from './utils/attendanceFulfillment';
import { SessionMetadataWorkspace } from './components/SessionMetadataWorkspace';
import { NoteReviewAndSignModal } from './components/NoteReviewAndSignModal';
import { NoteAutosaveRecoveryBanner } from './components/NoteAutosaveRecoveryBanner';
import { ProgressNoteStickyFooter } from './components/ProgressNoteStickyFooter';
import { formatNoteTextForCopy } from './utils/formatNoteTextForCopy';
import {
  useProviderNoteAutosave,
  type AutosaveStatus,
  type SaveIfChangedReason,
} from './hooks/useProviderNoteAutosave';
import { buildInlineMetadataFieldChecks } from './utils/inlineFieldChecks';
import {
  type NoteAutosaveSnapshot,
} from './utils/noteAutosaveSnapshot';
import { buildEncounterScrubberInput, buildEncounterScrubberParticipantAttendance } from '@/lib/encounterScrubber/buildEncounterScrubberInput';
import {
  applyAcceptedScrubberResults,
  buildEncounterScrubberReview,
  scrubberResultsHaveWarnings,
  scrubberResultsNeedUserReview,
  scrubEncounterCptCodes,
  validateEncounterScrubberMetadata,
  type EncounterScrubberResult,
  type MetadataValidationError,
  type ScrubberSuggestionDecision,
} from '@/lib/encounterScrubber/encounterScrubber';
import {
  useAppointmentSessionTypes,
  useCalendarPracticeId,
  useProviderSessionTypeEnablement,
} from '@/pages/calendar/appointmentTypeCatalog';
import { buildLocalDraftChecks } from '@/lib/localNoteChecks';
import {
  isAiNoteCheckBotAvailable,
  isAiNoteCheckBotUnavailableError,
  requestAiNoteCheck,
} from '@/lib/aiNoteCheckService';
import { readEncounterSessionTiming } from '@/lib/encounterSessionTiming';
import {
  FROZEN_TEMPLATE_CANONICAL_EXT,
  FROZEN_TEMPLATE_VERSION_EXT,
  FROZEN_TEMPLATE_DISPLAY_EXT,
} from '../settings/template-library/types';
import {
  getAllClientParticipantsFromAppointment,
  getPatientIdFromAppointment,
  getPractitionerIdFromAppointment,
  getQuestionnaireRefString,
  loadQuestionnaireForNote,
} from './utils/fhirHelpers';
import {
  getHealthcareServiceIdFromAppointment,
  embeddedOutcomeNoteTemplateOption,
  resolveDefaultAppointmentNoteTemplateId,
  resolveOutcomeNoteTemplateOption,
} from './utils/healthcareServiceNoteTemplate';
import {
  resolveNoteEditorSessionTypes,
  resolveProviderEligibleSessionTypeIds,
} from './utils/resolveNoteEditorSessionTypes';
import { getOutcomeSpecificNoteTemplateKind } from './utils/appointmentWorkflowStatus';
import {
  isOutcomeSpecificNoteQuestionnaire,
  OUTCOME_NOTE_TEMPLATE_IDS,
  type OutcomeNoteTemplateKind,
} from './noteTemplates';
import { expandAbsenceNoteQuestionnaire } from './utils/expandAbsenceNotePatientSections';
import {
  isProgressNoteDefaultResponseReady,
  questionnaireHasSessionModalityGate,
  upsertSessionModalityAnswer,
} from './utils/sessionModalityPrefill';
import { resolveAppointmentModality } from './utils/appointmentModality';
import { ABSENCE_REASON_PROTOTYPE_LINK_ID } from '@unify/fhir-model/constants';
import { ensureQuestionnaireOnServer } from './utils/ensureQuestionnaireOnServer';
import { sanitizeQuestionnaireResponseForCreate } from './utils/sanitizeQuestionnaireResponse';
import { getMissingRequiredFields } from './utils/questionnaireResponseRequired';
import { scrollToNoteCheckTarget } from './utils/scrollToNoteCheckTarget';
import { scrollToAndFlashQuestionnaireItem } from './utils/scrollToQuestionnaireItem';
import { scrollToNoteMetadataField } from './utils/scrollToNoteMetadataField';
import { isMedplumHttpStatus } from './utils/medplumClientErrors';
import { buildNoteSaveErrorMessage } from './noteSaveErrorMessage';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';
import { fhirCreateQR, fhirUpdateQR } from './utils/fhirWrite';
import {
  buildSessionParticipantAttendanceRows,
  DEFAULT_PARTICIPANT_ATTENDANCE_STATUS,
  getDefaultParticipantAttendanceFromAppointment,
  getExplicitParticipantAttendanceFromAppointment,
  getParticipantAttendanceFromResponseItems,
  getParticipantLocationFromResponseItems,
  getUnanimousParticipantAttendanceOutcomeKind,
  locationRequiredForParticipantAttendance,
  mergeParticipantAttendanceIntoResponseItems,
  participantAttendanceSignature,
  stripParticipantAttendanceGroup,
  type ParticipantAttendanceFhirStatus,
} from './utils/participantAttendance';
import {
  buildPracticeLocationOptionsFromAppointment,
  getPatientLocationOptions,
  nonUrlLocationsFromMetadata,
  resolveDefaultParticipantLocation,
} from './utils/patientLocationOptions';
import { getClientLocationLabels } from '@/lib/sessionLocation/clientLocationConfirmationFhir';
import {
  enrichAttendanceRowDisplayNames,
  resolveParticipantAttendanceRows,
} from './utils/resolveParticipantAttendanceRows';
import {
  buildQuestionnaireResponseItemsForSave,
  sanitizeResponseItemsForFhir,
  shapeResponseItemsForQuestionnaire,
} from './utils/questionnaireResponseShape';

/** Mantine Modal close transition, after which body scroll is unlocked. */
const REVIEW_MODAL_CLOSE_MS = 250;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AiNoteStatus = 'transcript-pending' | 'generating' | 'ai-draft' | 'generation-failed' | null;

/** Imperative API for AI flows to snapshot text answers before generation/regeneration. */
export interface ProgressNoteFormTextUndoApi {
  captureTextUndoBaselines: () => void;
  remountForm: () => void;
}

export interface ProgressNoteSaveOptions {
  isNew?: boolean;
  reason?: 'autosave' | 'sign' | 'finish' | 'leave';
}

export interface ProgressNoteFormProps {
  appointmentId: string;
  appointment?: Appointment | null;
  patientRef?: string;
  encounterRef?: string;
  encounter?: Encounter | null;
  chargeItems?: ChargeItem[];
  episodeOfCare?: EpisodeOfCare | null;
  existingResponse?: QuestionnaireResponse | null;
  existingQuestionnaire?: Questionnaire | null;
  /** @deprecated Previously used for "Load last note" feature. Kept for API compat. */
  previousResponse?: QuestionnaireResponse | null;
  onSave: (savedResponse: QuestionnaireResponse, options?: ProgressNoteSaveOptions) => void;
  onCancel: () => void;
  onAppointmentUpdated?: (appointment: Appointment) => void;
  /** If provided, called before sign; return false to cancel (e.g. confirm-read modal). */
  onBeforeSign?: () => Promise<boolean>;
  /** @deprecated Use onBeforeSign — kept for callers not yet migrated. */
  onBeforeSave?: () => Promise<boolean>;
  /** While appointment QR list / Questionnaire resolution is loading from Medplum (edit mode). */
  appointmentResponsesLoading?: boolean;
  /** Open the sign modal after review checks pass. */
  onRequestSign?: () => void;
  /** When editing a signed note (addendum flow), finish closes the editor without signing. */
  editIntent?: 'draft' | 'addendum';
  onFinishEdit?: () => void;
  /**
   * Addendum revise flow only: after quality checks and content save, before signature capture.
   * Return false to abort (e.g. user cancelled the revision-reason modal).
   */
  onBeforeResign?: (
    savedResponse: QuestionnaireResponse | null,
  ) => Promise<boolean>;
  /** Optional: appointment page registers capture/remount for AI text undo. */
  textUndoApiRef?: MutableRefObject<ProgressNoteFormTextUndoApi | null>;
  /** Autosave status for coordinating on-save check staleness. */
  onAutosaveStatusChange?: (status: AutosaveStatus) => void;
  /** While true, debounced autosave is disabled (e.g. AI generation in flight). */
  autosavePaused?: boolean;
  /** While true, note fields are read-only (e.g. AI generation in flight). */
  editorLocked?: boolean;
  /** When true, AI generation is disabled because the selected template is a late cancel / no-show note. */
  onOutcomeTemplateAiDisabledChange?: (disabled: boolean) => void;
  /** Extra chip rendered next to the Progress Note title (e.g. transcript status). */
  headerBadge?: ReactNode;
  /** Controls rendered at the top right of the Progress Note header (e.g. Generate Note). */
  headerActions?: ReactNode;
  /** Opens the same appointment edit modal as the header Edit control (Session Summary pencil). */
  onEditAppointment?: () => void;
  /**
   * Locations already shown on the appointment Participants table (QR + client confirmations +
   * header edits). Merged into note attendance so sign validation matches what the header shows.
   */
  externalParticipantLocationByPatientId?: Record<string, string | undefined>;
  /** Cohort appointment: badge next to the Progress Note title. */
  cohortNoteKind?: CohortNoteKind;
  /** Link to the shared Group note when viewing an individual fan-out copy. */
  sharedNoteHref?: string;
}

// ---------------------------------------------------------------------------
// Inner form component (re-mounts when template changes)
// ---------------------------------------------------------------------------

interface NoteFormBodyProps {
  questionnaire: Questionnaire;
  defaultResponse?: QuestionnaireResponse;
  /** Ref callback to expose formState to parent. */
  formStateRef: React.MutableRefObject<QuestionnaireFormState | null>;
  /** Optional: show review comments inline under each form section (supervision). */
  commentsByPath?: Record<string, DocumentComment[]>;
  onResolveComment?: (commentId: string) => void;
  canResolve?: boolean;
  onFormChange?: () => void;
  /** Called once when the questionnaire form has hydrated (establishes autosave baseline). */
  onFormReady?: () => void;
  onRegisterTextUndoCapture?: (capture: () => void) => void;
  readOnly?: boolean;
}

const NoteFormBody = memo(function NoteFormBody({
  questionnaire,
  defaultResponse,
  formStateRef,
  commentsByPath,
  onResolveComment,
  canResolve = false,
  onFormChange,
  onFormReady,
  onRegisterTextUndoCapture,
  readOnly = false,
}: NoteFormBodyProps): JSX.Element {
  const formState = useQuestionnaireForm({
    questionnaire,
    defaultValue: defaultResponse,
    disablePagination: false,
  });
  const questionnaireItems = questionnaire.item;

  const baselineSyncedRef = useRef(false);
  const lastScheduledItemsRef = useRef<string | null>(null);
  const formChangeDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    baselineSyncedRef.current = false;
    lastScheduledItemsRef.current = null;
  }, [defaultResponse?.id, questionnaire.id]);

  const getResponse = useCallback(() => {
    if ('loading' in formState && formState.loading) return undefined;
    return 'questionnaireResponse' in formState ? formState.questionnaireResponse : undefined;
  }, [formState]);

  // Expose formState to parent
  useEffect(() => {
    formStateRef.current = formState;
    return () => {
      formStateRef.current = null;
    };
  }, [formState, formStateRef]);

  useEffect(() => {
    if (formState.loading) return;

    if (!baselineSyncedRef.current) {
      baselineSyncedRef.current = true;
      lastScheduledItemsRef.current = JSON.stringify(formState.questionnaireResponse?.item ?? []);
      onFormReady?.();
      return;
    }

    if (!onFormChange) return;

    if (formChangeDebounceRef.current) {
      window.clearTimeout(formChangeDebounceRef.current);
    }
    formChangeDebounceRef.current = window.setTimeout(() => {
      formChangeDebounceRef.current = null;
      const current = JSON.stringify(formState.questionnaireResponse?.item ?? []);
      if (current === lastScheduledItemsRef.current) return;
      lastScheduledItemsRef.current = current;
      onFormChange();
    }, 120);

    return () => {
      if (formChangeDebounceRef.current) {
        window.clearTimeout(formChangeDebounceRef.current);
        formChangeDebounceRef.current = null;
      }
    };
  }, [formState, onFormChange, onFormReady]);

  if (formState.loading) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        Loading form…
      </Text>
    );
  }

  if (formState.items.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        This template has no items.
      </Text>
    );
  }

  // Multi-page (pagination)
  if (formState.pagination) {
    const { pages, activePage, onNextPage, onPrevPage } = formState;
    const showBack = activePage > 0;
    const showNext = activePage < pages.length - 1;

    return (
      <QuestionnaireTextUndoProvider
        questionnaire={questionnaire}
        getResponse={getResponse}
        onRegisterCapture={onRegisterTextUndoCapture}
      >
        <form onSubmit={(e) => e.preventDefault()}>
        <Stepper active={activePage} allowNextStepsSelect={false} size="sm">
          {pages.map((page, pageIndex) => (
            <Stepper.Step key={page.linkId} label={page.title}>
              {pageIndex === activePage && (
                <PreviewItemArray
                  formState={formState}
                  context={[]}
                  items={formState.items}
                  responseItems={formState.responseItems}
                  questionnaireItems={questionnaireItems}
                  commentsByPath={commentsByPath}
                  onResolveComment={onResolveComment}
                  canResolve={canResolve && !readOnly}
                  readOnly={readOnly}
                />
              )}
            </Stepper.Step>
          ))}
        </Stepper>
        <Group justify="center" gap="sm" mt="md">
          {showBack && (
            <Button variant="default" size="sm" onClick={onPrevPage} disabled={readOnly}>
              Back
            </Button>
          )}
          {showNext && (
            <Button
              size="sm"
              disabled={readOnly}
              onClick={(e) => {
                const form = e.currentTarget.closest('form') as HTMLFormElement;
                if (form.reportValidity()) {
                  onNextPage();
                }
              }}
            >
              Next
            </Button>
          )}
        </Group>
      </form>
      </QuestionnaireTextUndoProvider>
    );
  }

  // Single page
  return (
    <QuestionnaireTextUndoProvider
      questionnaire={questionnaire}
      getResponse={getResponse}
      onRegisterCapture={onRegisterTextUndoCapture}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <PreviewItemArray
          formState={formState}
          context={[]}
          items={formState.items}
          responseItems={formState.responseItems}
          questionnaireItems={questionnaireItems}
          commentsByPath={commentsByPath}
          onResolveComment={onResolveComment}
          canResolve={canResolve && !readOnly}
          readOnly={readOnly}
        />
      </form>
    </QuestionnaireTextUndoProvider>
  );
});

// ---------------------------------------------------------------------------
// Main form component
// ---------------------------------------------------------------------------

export function ProgressNoteForm({
  appointmentId,
  appointment,
  patientRef,
  encounterRef,
  encounter,
  chargeItems = [],
  episodeOfCare,
  existingResponse,
  existingQuestionnaire,
  onSave,
  onCancel,
  onAppointmentUpdated,
  onBeforeSign,
  onBeforeSave,
  appointmentResponsesLoading = false,
  onRequestSign,
  editIntent = 'draft',
  onFinishEdit,
  onBeforeResign,
  textUndoApiRef,
  onAutosaveStatusChange,
  autosavePaused = false,
  editorLocked = false,
  onOutcomeTemplateAiDisabledChange,
  headerBadge,
  headerActions,
  onEditAppointment,
  externalParticipantLocationByPatientId,
  cohortNoteKind,
  sharedNoteHref,
}: ProgressNoteFormProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  const isSigned = !!existingResponse?.extension?.some((e) => e.url === PROVIDER_SIGNATURE_EXT_URL);

  const useMockTemplateLibrary = false;

  /**
   * Subject for QuestionnaireResponse — prefer `patientRef` from the page (AppointmentPage resolves
   * the chart patient from Encounter.subject when an encounter exists, else first Patient on the Appointment).
   * Cohort shared notes use the Group as subject.
   */
  const resolvedPatientRef = useMemo(() => {
    if (patientRef) return patientRef;
    if (appointment) {
      const pid = getPatientIdFromAppointment(appointment);
      return pid ? `Patient/${pid}` : undefined;
    }
    return undefined;
  }, [patientRef, appointment]);

  const isCohortSharedNote = useMemo(() => {
    if (existingResponse?.subject?.reference?.startsWith('Group/')) return true;
    // Patient-subject fan-out copies are not the shared note.
    if (existingResponse?.subject?.reference?.startsWith('Patient/')) return false;
    return Boolean(appointment && isCohortGroupAppointment(appointment));
  }, [existingResponse?.subject?.reference, appointment]);

  const cohortGroupSubjectRef = useMemo(() => {
    if (existingResponse?.subject?.reference?.startsWith('Group/')) {
      return existingResponse.subject.reference;
    }
    if (appointment) {
      return getAppointmentGroupRef(appointment)?.reference;
    }
    return undefined;
  }, [existingResponse?.subject?.reference, appointment]);
  const { templates, loading: templatesLoading } = useNoteTemplates({
    skip: useMockTemplateLibrary,
  });
  /** Same appointment-note pool used for picker + HealthcareService note-template-id resolution. */
  const allAppointmentNoteTemplates = templates;
  const allTemplatesLoading = templatesLoading;

  /** Mock mode: embedded Questionnaire definitions only. Live Medplum: server Questionnaires only (no fallback). */
  const effectiveTemplates = useMemo(() => {
    if (useMockTemplateLibrary) {
      return getMockAppointmentNoteTemplates();
    }
    return templates;
  }, [useMockTemplateLibrary, templates]);

  // Ref to the inner form's state
  const formStateRef = useRef<QuestionnaireFormState | null>(null);
  /** Stable merge baseline for save/diff — updated on load and after each successful save. */
  const existingItemsBaselineRef = useRef<QuestionnaireResponseItem[] | undefined>(undefined);
  /** Latest saved QR for update-after-create while add-mode keeps existingResponse null in props. */
  const persistedResponseSnapshotRef = useRef<QuestionnaireResponse | null>(existingResponse ?? null);
  const formInstanceKeyRef = useRef(existingResponse?.id ?? `session-${appointmentId}`);
  const stableDefaultResponseRef = useRef<QuestionnaireResponse | undefined>(undefined);
  const sessionTiming = useMemo(() => readEncounterSessionTiming(encounter), [encounter]);
  const allSessionTypes = useAppointmentSessionTypes();
  const appointmentPractitionerId = useMemo(
    () => (appointment ? getPractitionerIdFromAppointment(appointment) : undefined),
    [appointment],
  );
  const appointmentPractitionerStub = useMemo(
    () =>
      appointmentPractitionerId
        ? { resourceType: 'Practitioner' as const, id: appointmentPractitionerId }
        : undefined,
    [appointmentPractitionerId],
  );
  const appointmentPracticeId = useCalendarPracticeId(appointmentPractitionerStub);
  const providerSessionRows = useProviderSessionTypeEnablement(
    appointmentPracticeId,
    appointmentPractitionerId ?? '',
  );
  const serviceTypeOptions = useMemo(() => {
    const providerEligibleIds = resolveProviderEligibleSessionTypeIds(providerSessionRows);
    const currentHsId = appointment
      ? getHealthcareServiceIdFromAppointment(appointment)
      : undefined;
    return resolveNoteEditorSessionTypes({
      allSessionTypes,
      providerEligibleIds,
      currentHealthcareServiceId: currentHsId,
    });
  }, [allSessionTypes, appointment, providerSessionRows]);
  const healthcareServiceDurationMinutes = useMemo(() => {
    if (!appointment) return undefined;
    const hsId = getHealthcareServiceIdFromAppointment(appointment);
    if (!hsId) return undefined;
    return (
      serviceTypeOptions.find((st) => st.id === hsId)?.durationMinutes
      ?? allSessionTypes.find((st) => st.id === hsId)?.durationMinutes
    );
  }, [appointment, serviceTypeOptions, allSessionTypes]);
  const encounterTimingKey = useMemo(
    () => JSON.stringify(sessionTiming ?? null),
    [sessionTiming],
  );

  // --- Template selection ---
  const userPickedTemplateRef = useRef(false);
  const defaultTemplateAppliedRef = useRef(false);
  const userEditedBillingRef = useRef(false);
  const lastAttendanceOutcomeKindRef = useRef<OutcomeNoteTemplateKind | undefined>(undefined);
  const lastAbsenceRosterKeyRef = useRef('');
  const [outcomeTemplateSuggestion, setOutcomeTemplateSuggestion] = useState<{
    kind: 'late-cancel' | 'no-show';
    templateId: string;
  } | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    existingQuestionnaire?.id ?? null,
  );

  useEffect(() => {
    userPickedTemplateRef.current = false;
    defaultTemplateAppliedRef.current = false;
    userEditedBillingRef.current = false;
    lastAttendanceOutcomeKindRef.current = undefined;
    lastAbsenceRosterKeyRef.current = '';
    setOutcomeTemplateSuggestion(null);
    persistedResponseSnapshotRef.current = existingResponse ?? null;
    formInstanceKeyRef.current = existingResponse?.id ?? `session-${appointmentId}`;
    stableDefaultResponseRef.current = undefined;
// eslint-disable-next-line react-hooks/exhaustive-deps -- resets the per-appointment refs when the appointment changes; depending on existingResponse would clear userPickedTemplate/userEditedBilling and the form key on every response update
  }, [appointmentId, appointment]);

  // Default new notes to outcome-specific or HealthcareService note-template-id only.
  useEffect(() => {
    if (existingResponse?.id) return;
    if (userPickedTemplateRef.current) return;
    if (defaultTemplateAppliedRef.current) return;
    if (templatesLoading || allTemplatesLoading) return;

    let cancelled = false;

    void (async () => {
      let defaultId: string | null = null;
      if (useMockTemplateLibrary) {
        const outcomeKind = appointment ? getOutcomeSpecificNoteTemplateKind(appointment) : undefined;
        if (outcomeKind) {
          defaultId = resolveOutcomeNoteTemplateOption(effectiveTemplates, outcomeKind).id;
        } else if (appointment) {
          const hsId = getHealthcareServiceIdFromAppointment(appointment);
          const hsDefault = hsId
            ? allSessionTypes.find((st) => st.id === hsId)?.noteTemplateId
            : undefined;
          if (hsDefault) {
            defaultId =
              effectiveTemplates.find((t) => t.id === hsDefault)?.id
              ?? getMockAppointmentNoteTemplates().find((t) => t.id === hsDefault)?.id
              ?? null;
          }
        }
      } else {
        defaultId = await resolveDefaultAppointmentNoteTemplateId(
          medplum,
          appointment,
          effectiveTemplates,
          { allTemplates: allAppointmentNoteTemplates },
        );
      }
      if (cancelled) return;
      defaultTemplateAppliedRef.current = true;
      if (defaultId) {
        setSelectedTemplateId(defaultId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    appointment,
    medplum,
    effectiveTemplates,
    allAppointmentNoteTemplates,
    allSessionTypes,
    existingResponse?.id,
    templatesLoading,
    allTemplatesLoading,
    useMockTemplateLibrary,
  ]);

  // Keep template dropdown aligned with the server Questionnaire once it loads (edit mode).
  useEffect(() => {
    if (existingQuestionnaire?.id) {
      setSelectedTemplateId(existingQuestionnaire.id);
    }
  }, [existingQuestionnaire?.id]);

  const selectedTemplate: NoteTemplateOption | undefined = useMemo(() => {
    if (!selectedTemplateId) return undefined;
    const match =
      effectiveTemplates.find((t) => t.id === selectedTemplateId)
      ?? allAppointmentNoteTemplates.find((t) => t.id === selectedTemplateId);
    if (match) return match;
    if (selectedTemplateId === OUTCOME_NOTE_TEMPLATE_IDS.cancelled) {
      return embeddedOutcomeNoteTemplateOption('cancelled');
    }
    if (
      selectedTemplateId === OUTCOME_NOTE_TEMPLATE_IDS['late-cancel']
      || selectedTemplateId === OUTCOME_NOTE_TEMPLATE_IDS['no-show']
      || selectedTemplateId === OUTCOME_NOTE_TEMPLATE_IDS.mixed
      || selectedTemplateId === 'late-cancel-session-note'
      || selectedTemplateId === 'no-show-session-note'
    ) {
      return embeddedOutcomeNoteTemplateOption('late-cancel');
    }
    return undefined;
  }, [effectiveTemplates, allAppointmentNoteTemplates, selectedTemplateId]);

  /** Fallback when parent hook could not resolve Questionnaire (ref shape, permissions) — same ref as QR. */
  const [clientResolvedQuestionnaire, setClientResolvedQuestionnaire] = useState<Questionnaire | null>(null);
  const [clientQuestionnaireLoading, setClientQuestionnaireLoading] = useState(false);

  useEffect(() => {
    if (useMockTemplateLibrary || !existingResponse?.id || existingQuestionnaire) {
      setClientResolvedQuestionnaire(null);
      setClientQuestionnaireLoading(false);
      return;
    }
    if (appointmentResponsesLoading) {
      setClientQuestionnaireLoading(false);
      return;
    }
    const ref = getQuestionnaireRefString(existingResponse.questionnaire);
    if (!ref) {
      setClientResolvedQuestionnaire(null);
      setClientQuestionnaireLoading(false);
      return;
    }
    let cancelled = false;
    setClientQuestionnaireLoading(true);
    void loadQuestionnaireForNote(medplum, ref)
      .then((q) => {
        if (!cancelled) setClientResolvedQuestionnaire(q);
      })
      .finally(() => {
        if (!cancelled) setClientQuestionnaireLoading(false);
      });
    return () => {
      cancelled = true;
      setClientQuestionnaireLoading(false);
    };
  }, [
    medplum,
    useMockTemplateLibrary,
    existingResponse?.id,
    existingResponse?.questionnaire,
    existingQuestionnaire,
    appointmentResponsesLoading,
  ]);

  // Prefer the selected template's questionnaire when the selection diverges from the saved note.
  // existingQuestionnaire/clientResolvedQuestionnaire reflect the saved note's questionnaire, which
  // should only be used when the user hasn't switched to a different template (e.g. edit mode).
  const savedQuestionnaireMatchesSelection = useMemo(() => {
    const q = existingQuestionnaire ?? clientResolvedQuestionnaire;
    if (!q || !selectedTemplateId) return !selectedTemplateId; // no selection → use saved
    return (
      q.id === selectedTemplateId ||
      q.url?.includes(`/${selectedTemplateId}`) ||
      q.identifier?.some((i) => i.value === selectedTemplateId)
    );
  }, [existingQuestionnaire, clientResolvedQuestionnaire, selectedTemplateId]);

  const questionnaire = savedQuestionnaireMatchesSelection
    ? (existingQuestionnaire ?? clientResolvedQuestionnaire ?? selectedTemplate?.questionnaire ?? null)
    : (selectedTemplate?.questionnaire ?? null);

  useEffect(() => {
    onOutcomeTemplateAiDisabledChange?.(isOutcomeSpecificNoteQuestionnaire(questionnaire));
  }, [questionnaire, onOutcomeTemplateAiDisabledChange]);

  const applyOutcomeTemplateSuggestion = useCallback(() => {
    if (!outcomeTemplateSuggestion) return;
    setSelectedTemplateId(outcomeTemplateSuggestion.templateId);
    setFormKey((k) => k + 1);
    setOutcomeTemplateSuggestion(null);
    defaultTemplateAppliedRef.current = true;
  }, [outcomeTemplateSuggestion]);

  // Form key: changes force remount of NoteFormBody (fresh useQuestionnaireForm state)
  const [formKey, setFormKey] = useState(0);
  const captureTextUndoRef = useRef<(() => void) | null>(null);
  const flushDebouncedRef = useRef<() => void>(() => {});
  const lastAppliedAiDraftItemsRef = useRef<string | null>(null);
  const registerTextUndoCapture = useCallback((capture: () => void) => {
    captureTextUndoRef.current = capture;
  }, []);

  const remountNoteFormFromServer = useCallback(() => {
    flushDebouncedRef.current();
    stableDefaultResponseRef.current = undefined;
    if (existingResponse?.id) {
      persistedResponseSnapshotRef.current = existingResponse;
      existingItemsBaselineRef.current = existingResponse.item
        ? stripParticipantAttendanceGroup(existingResponse.item)
        : undefined;
    }
    setFormKey((k) => k + 1);
  }, [existingResponse]);

  useEffect(() => {
    if (!textUndoApiRef) return;
    textUndoApiRef.current = {
      captureTextUndoBaselines: () => captureTextUndoRef.current?.(),
      remountForm: remountNoteFormFromServer,
    };
    return () => {
      textUndoApiRef.current = null;
    };
  }, [textUndoApiRef, remountNoteFormFromServer]);

  const [manualEditFromAiDraft, setManualEditFromAiDraft] = useState(false);

  const initialMetadataRef = useRef<NoteMetadataSnapshot | null>(null);
  const practiceLocationOptions = useMemo(
    () => (appointment ? buildPracticeLocationOptionsFromAppointment(appointment) : []),
    [appointment],
  );
  /** Patient id → the client's confirmed session location (the Encounter's, else older Appointment data). */
  const clientLocationLabels = useMemo(
    () => getClientLocationLabels(encounter, appointment),
    [encounter, appointment],
  );
  const [noteMetadata, setNoteMetadata] = useState<NoteMetadataSnapshot | null>(null);

  // Stable identity (ref-backed) — also passed deep into memoized form rows.
  const existingResponseRef = useRef(existingResponse);
  existingResponseRef.current = existingResponse;
  useEffect(() => {
    const items = existingResponseRef.current?.item;
    existingItemsBaselineRef.current = items ? stripParticipantAttendanceGroup(items) : undefined;
  }, [existingResponse?.id, existingResponse?.meta?.versionId, formKey]);

  const serverAiDraftClinicalJson = useMemo(() => {
    if (!existingResponse?.id || getDetailedStatus(existingResponse) !== 'ai-draft') {
      return null;
    }
    return JSON.stringify(stripParticipantAttendanceGroup(existingResponse.item));
  }, [existingResponse]);

  useEffect(() => {
    if (serverAiDraftClinicalJson == null) {
      lastAppliedAiDraftItemsRef.current = null;
      return;
    }
    if (manualEditFromAiDraft) return;
    if (lastAppliedAiDraftItemsRef.current === serverAiDraftClinicalJson) return;
    lastAppliedAiDraftItemsRef.current = serverAiDraftClinicalJson;
    remountNoteFormFromServer();
  }, [serverAiDraftClinicalJson, manualEditFromAiDraft, remountNoteFormFromServer]);

  useEffect(() => {
    if (!appointment) {
      setNoteMetadata(null);
      initialMetadataRef.current = null;
      return;
    }
    const resolved = resolveNoteMetadataForEditor({
      existingResponse,
      appointment,
      encounter,
      chargeItems,
      episodeOfCare,
      healthcareServiceDurationMinutes,
    });
    const enriched =
      serviceTypeOptions.length > 0 || allSessionTypes.length > 0
        ? enrichBillingCodesWithCpt(
            resolved,
            serviceTypeOptions.length > 0 ? serviceTypeOptions : allSessionTypes,
          )
        : resolved;

    setNoteMetadata((prev) => {
      if (userEditedBillingRef.current && prev?.billingCodes?.length) {
        return {
          ...enriched,
          billingCodes: prev.billingCodes,
          units: prev.units ?? enriched.units,
          modality: prev.modality ?? enriched.modality,
          durationMinutes: prev.durationMinutes ?? enriched.durationMinutes,
          locations: prev.locations ?? enriched.locations,
        };
      }
      return enriched;
    });
    // Baseline must match editor shape (enriched live billing). QR snapshots omit
    // billingCodes by design — using getNoteMetadataFromQR here false-diffs CPT on every save.
    if (!userEditedBillingRef.current) {
      initialMetadataRef.current = enriched;
    }
// eslint-disable-next-line react-hooks/exhaustive-deps -- metadata is re-derived only when the appointment/encounter fields listed here change; adding the whole appointment/encounter/episodeOfCare/existingResponse objects would re-run on every identity change and overwrite in-progress metadata edits
  }, [
    appointment?.id,
    appointment?.start,
    appointment?.end,
    appointment?.minutesDuration,
    existingResponse?.id,
    encounter?.id,
    encounterTimingKey,
    healthcareServiceDurationMinutes,
    serviceTypeOptions,
    allSessionTypes,
    chargeItems,
    episodeOfCare?.id,
  ]);

  // --- Saving / auto-save state ---
  const [saving, setSaving] = useState(false);
  const persistSaveInFlightRef = useRef(false);
  const [signing, setSigning] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewChecksLoading, setReviewChecksLoading] = useState(false);
  const [reviewChecksError, setReviewChecksError] = useState<string | null>(null);
  const [reviewDraftChecks, setReviewDraftChecks] = useState<AiNoteCheckResult[]>([]);
  const [reviewAutomaticChecks, setReviewAutomaticChecks] = useState<AiNoteCheckResult[]>([]);
  const [reviewBlocked, setReviewBlocked] = useState(false);
  const [reviewBlockedMessage, setReviewBlockedMessage] = useState<string | undefined>();
  const [reviewContinueAction, setReviewContinueAction] = useState<'sign' | 'finish'>('sign');
  const [scrubberResults, setScrubberResults] = useState<EncounterScrubberResult[]>([]);
  const [scrubberMetadataErrors, setScrubberMetadataErrors] = useState<MetadataValidationError[]>(
    [],
  );
  const [scrubberValidationErrors, setScrubberValidationErrors] = useState<Record<string, string>>(
    {},
  );
  const [inlineFieldErrors, setInlineFieldErrors] = useState<Record<string, string>>({});
  const [autosaveSavedAt, setAutosaveSavedAt] = useState<Date | null>(null);
  const pendingReviewRef = useRef<{
    metadata: NoteMetadataSnapshot;
    reviewableResults: EncounterScrubberResult[];
    afterConfirm: 'sign' | 'finish';
  } | null>(null);
  const checkGenerationRef = useRef(0);

  // --- Participant attendance (FHIR, persisted in QuestionnaireResponse.item) ---
  const attendanceRowsBase = useMemo(() => {
    if (!appointment) return [] as { patientId: string; displayName: string }[];
    return resolveParticipantAttendanceRows({
      appointment,
      encounter,
      episodeOfCare,
      // Chart patientRef is still Patient/… for shared cohort notes; pass the note subject
      // and isSharedCohortNote so attendance lists every appointment participant.
      notePatientRef: isCohortSharedNote
        ? (cohortGroupSubjectRef ?? patientRef)
        : patientRef,
      isSharedCohortNote: isCohortSharedNote,
    });
  }, [
    appointment,
    encounter,
    episodeOfCare,
    patientRef,
    isCohortSharedNote,
    cohortGroupSubjectRef,
  ]);

  const [attendanceByPatient, setAttendanceByPatient] = useState<
    Record<string, ParticipantAttendanceFhirStatus | undefined>
  >({});
  const [savedAttendanceByPatient, setSavedAttendanceByPatient] = useState<
    Record<string, ParticipantAttendanceFhirStatus | undefined>
  >({});
  const [locationByPatient, setLocationByPatient] = useState<Record<string, string | undefined>>({});
  const [participantLocationErrors, setParticipantLocationErrors] = useState<
    Record<string, string>
  >({});
  const [patientsById, setPatientsById] = useState<Record<string, Patient | undefined>>({});
  const [relatedPersonsById, setRelatedPersonsById] = useState<
    Record<string, RelatedPerson | undefined>
  >({});

  const attendanceRows = useMemo(
    () => enrichAttendanceRowDisplayNames(attendanceRowsBase, patientsById, relatedPersonsById),
    [attendanceRowsBase, patientsById, relatedPersonsById],
  );

  const isCohortPatientNoteCopy = useMemo(() => {
    if (!existingResponse?.subject?.reference?.startsWith('Patient/')) return false;
    return Boolean(appointment && isCohortGroupAppointment(appointment));
  }, [existingResponse?.subject?.reference, appointment]);

  /** Expand patient-section / absence-reason prototypes for the current roster. */
  const formQuestionnaire = useMemo(() => {
    if (!questionnaire) return null;
    const participants = attendanceRows.map((row) => ({
      patientId: row.patientId,
      displayName: row.displayName,
    }));

    let next = questionnaire;
    const shouldExpandGroup =
      isCohortSharedNote ||
      isCohortPatientNoteCopy ||
      (questionnaire.item?.some(isGroupNotePatientSectionPrototype) ?? false);
    if (shouldExpandGroup) {
      if (isCohortPatientNoteCopy) {
        const subjectRef = existingResponse?.subject?.reference ?? patientRef ?? '';
        const patientId = subjectRef.startsWith('Patient/')
          ? subjectRef.slice('Patient/'.length)
          : undefined;
        if (!patientId) {
          next = expandGroupNoteQuestionnaire(questionnaire, participants);
        } else {
          const withSelf = participants.some((p) => p.patientId === patientId)
            ? participants
            : [
                ...participants,
                {
                  patientId,
                  displayName: existingResponse?.subject?.display?.trim() || patientId,
                },
              ];
          next = expandGroupNoteQuestionnaire(questionnaire, withSelf, {
            keepOnlyPatientId: patientId,
          });
        }
      } else {
        next = expandGroupNoteQuestionnaire(questionnaire, participants);
      }
    }

    if (next.item?.some((item) => item.linkId === ABSENCE_REASON_PROTOTYPE_LINK_ID)) {
      next = expandAbsenceNoteQuestionnaire(next, participants, attendanceByPatient);
    }
    return next;
  }, [
    questionnaire,
    attendanceRows,
    attendanceByPatient,
    isCohortSharedNote,
    isCohortPatientNoteCopy,
    existingResponse?.subject?.reference,
    existingResponse?.subject?.display,
    patientRef,
  ]);

  /** Include displayName so NoteFormBody remounts when Patient.name enrichment replaces "Client". */
  const cohortPatientSectionKey = useMemo(() => {
    if (!isCohortSharedNote && !isCohortPatientNoteCopy) return '';
    return attendanceRows
      .map((row) => `${row.patientId}:${row.displayName}`)
      .sort()
      .join(',');
  }, [attendanceRows, isCohortSharedNote, isCohortPatientNoteCopy]);

  const prevCohortPatientSectionKeyRef = useRef(cohortPatientSectionKey);
  useEffect(() => {
    if (!isCohortSharedNote && !isCohortPatientNoteCopy) return;
    if (prevCohortPatientSectionKeyRef.current === cohortPatientSectionKey) return;
    prevCohortPatientSectionKeyRef.current = cohortPatientSectionKey;
    const state = formStateRef.current;
    if (state && !('loading' in state && state.loading) && 'questionnaireResponse' in state) {
      stableDefaultResponseRef.current = {
        ...state.questionnaireResponse,
        item: stripParticipantAttendanceGroup(state.questionnaireResponse.item),
      };
    } else {
      stableDefaultResponseRef.current = undefined;
    }
    setFormKey((k) => k + 1);
  }, [cohortPatientSectionKey, isCohortSharedNote, isCohortPatientNoteCopy]);

  const scrubberParticipantAttendance = useMemo(
    () =>
      buildEncounterScrubberParticipantAttendance({
        episodeOfCare,
        notePatientRef: patientRef,
        attendanceRows,
        attendanceByPatientId: attendanceByPatient,
      }),
    [episodeOfCare, patientRef, attendanceRows, attendanceByPatient],
  );

  const sessionParticipantsForOutcome = useMemo(
    () => buildSessionParticipantAttendanceRows(attendanceRows, appointment),
    [attendanceRows, appointment],
  );

  const attendanceOutcomeKind = useMemo(
    () =>
      getUnanimousParticipantAttendanceOutcomeKind(
        sessionParticipantsForOutcome,
        attendanceByPatient,
      ),
    [sessionParticipantsForOutcome, attendanceByPatient],
  );

  /** True when all participants are absent (late cancel, no show, or cancelled) — billing/quality checks don't apply. */
  const isOutcomeNote = !!attendanceOutcomeKind;

  const absenceRosterKey = useMemo(
    () =>
      attendanceOutcomeKind
        ? sessionParticipantsForOutcome
            .map((row) => `${row.patientId}:${attendanceByPatient[row.patientId] ?? ''}`)
            .join('|')
        : '',
    [attendanceOutcomeKind, sessionParticipantsForOutcome, attendanceByPatient],
  );

  const selectableTemplates = useMemo(() => {
    if (attendanceOutcomeKind) {
      return [resolveOutcomeNoteTemplateOption(effectiveTemplates, attendanceOutcomeKind)];
    }
    if (
      selectedTemplate
      && !effectiveTemplates.some((t) => t.id === selectedTemplate.id)
    ) {
      return [selectedTemplate, ...effectiveTemplates];
    }
    return effectiveTemplates;
  }, [effectiveTemplates, attendanceOutcomeKind, selectedTemplate]);

  const templateSelectData = useMemo(
    () => selectableTemplates.map((t) => ({ value: t.id, label: t.title })),
    [selectableTemplates],
  );

  // All session participants absent (late cancel / no show / cancelled) → auto-switch template; otherwise restore default.
  useEffect(() => {
    // Don't override signed notes.
    if (isSigned) return;

    const kind = attendanceOutcomeKind;
    const prevKind = lastAttendanceOutcomeKindRef.current;
    const rosterChanged = absenceRosterKey !== lastAbsenceRosterKeyRef.current;
    if (kind === prevKind && !rosterChanged) return;
    lastAttendanceOutcomeKindRef.current = kind;
    lastAbsenceRosterKeyRef.current = absenceRosterKey;

    if (kind) {
      // Outcome templates have embedded fallbacks — don't wait for effectiveTemplates.
      const match = resolveOutcomeNoteTemplateOption(effectiveTemplates, kind);
      setSelectedTemplateId(match.id);
      stableDefaultResponseRef.current = undefined;
      setFormKey((k) => k + 1);
      setOutcomeTemplateSuggestion(null);
      userPickedTemplateRef.current = false;
      defaultTemplateAppliedRef.current = true;
      return;
    }

    // Attendance moved away from unanimous outcome — revert to session default.
    // Only revert if templates have loaded (we need effectiveTemplates to pick the right default).
    if (!prevKind) return;
    if (templatesLoading || allTemplatesLoading) return;

    let cancelled = false;
    void (async () => {
      let defaultId: string | null = null;
      if (useMockTemplateLibrary) {
        const hsId = appointment ? getHealthcareServiceIdFromAppointment(appointment) : undefined;
        const hsDefault = hsId
          ? allSessionTypes.find((st) => st.id === hsId)?.noteTemplateId
          : undefined;
        defaultId = hsDefault
          ? (effectiveTemplates.find((t) => t.id === hsDefault)?.id ?? null)
          : null;
      } else {
        defaultId = await resolveDefaultAppointmentNoteTemplateId(
          medplum,
          appointment,
          effectiveTemplates,
          { ignoreAppointmentOutcome: true, allTemplates: allAppointmentNoteTemplates },
        );
      }
      if (cancelled || defaultId === selectedTemplateId) return;
      setSelectedTemplateId(defaultId);
      stableDefaultResponseRef.current = undefined;
      setFormKey((k) => k + 1);
      userPickedTemplateRef.current = false;
      defaultTemplateAppliedRef.current = true;
      setOutcomeTemplateSuggestion(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    attendanceOutcomeKind,
    absenceRosterKey,
    appointment,
    effectiveTemplates,
    allAppointmentNoteTemplates,
    allSessionTypes,
    isSigned,
    medplum,
    selectedTemplateId,
    templatesLoading,
    allTemplatesLoading,
    useMockTemplateLibrary,
  ]);

  const attendanceRowKey = attendanceRowsBase.map((r) => r.patientId).join('|');
  const attendanceInitKey = `${existingResponse?.id ?? 'new'}:${appointmentId}`;
  // Re-hydrate when attendance changes on the Appointment elsewhere (appointment page, session bots).
  const attendanceSignature = participantAttendanceSignature(appointment);
  const attendanceHydratedForRef = useRef<string | null>(null);

  useEffect(() => {
    const ids = attendanceRowKey.split('|').filter(Boolean);
    if (ids.length === 0) {
      setPatientsById({});
      setRelatedPersonsById({});
      return;
    }
    const relatedIds = new Set(
      appointment
        ? getAllClientParticipantsFromAppointment(appointment)
            .filter((p) => p.actorReference?.startsWith('RelatedPerson/'))
            .map((p) => p.actorReference!.replace(/^RelatedPerson\//, '').trim())
            .filter(Boolean)
        : [],
    );
    let cancelled = false;
    void Promise.all(
      ids.map(async (id) => {
        if (relatedIds.has(id)) {
          try {
            return ['related', id, await medplum.readResource('RelatedPerson', id)] as const;
          } catch {
            return ['related', id, undefined] as const;
          }
        }
        try {
          return ['patient', id, await medplum.readResource('Patient', id)] as const;
        } catch {
          return ['patient', id, undefined] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      const patients: Record<string, Patient | undefined> = {};
      const related: Record<string, RelatedPerson | undefined> = {};
      for (const [kind, id, resource] of entries) {
        if (kind === 'related') {
          related[id] = resource as RelatedPerson | undefined;
        } else {
          patients[id] = resource as Patient | undefined;
        }
      }
      setPatientsById(patients);
      setRelatedPersonsById(related);
    });
    return () => {
      cancelled = true;
    };
  }, [medplum, attendanceRowKey, appointment]);

  const participantLocationOptionsByPatientId = useMemo(() => {
    const out: Record<string, string[]> = {};
    const fromApptLocation = clientLocationLabels;
    for (const row of attendanceRows) {
      const base = getPatientLocationOptions(
        patientsById[row.patientId],
        practiceLocationOptions,
      );
      const apptLabel = fromApptLocation[row.patientId];
      out[row.patientId] = apptLabel && !base.includes(apptLabel) ? [apptLabel, ...base] : base;
    }
    return out;
  }, [attendanceRows, patientsById, practiceLocationOptions, clientLocationLabels]);

  useEffect(() => {
    if (attendanceRows.length === 0) {
      setAttendanceByPatient({});
      setSavedAttendanceByPatient({});
      setLocationByPatient({});
      attendanceHydratedForRef.current = null;
      return;
    }

    const hydrateKey = `${attendanceInitKey}|${attendanceRowKey}|${appointment?.id ?? 'no-appt'}|${attendanceSignature}`;
    if (attendanceHydratedForRef.current === hydrateKey) {
      return;
    }
    attendanceHydratedForRef.current = hydrateKey;

    const fromQr = existingResponse?.id
      ? getParticipantAttendanceFromResponseItems(existingResponse.item)
      : {};
    const fromQrLocation = existingResponse?.id
      ? getParticipantLocationFromResponseItems(existingResponse.item)
      : {};
    const fromAppt = appointment
      ? getDefaultParticipantAttendanceFromAppointment(appointment)
      : {};
    // Explicit Appointment attendance is what fan-out reads, so it outranks a stale note copy.
    const fromApptExplicit = appointment
      ? getExplicitParticipantAttendanceFromAppointment(appointment)
      : {};
    const fromApptLocation = clientLocationLabels;
    const nextAttendance: Record<string, ParticipantAttendanceFhirStatus> = {
      ...fromAppt,
      ...fromQr,
      ...fromApptExplicit,
    };
    const legacyLocations = nonUrlLocationsFromMetadata(noteMetadata?.locations);
    setLocationByPatient((prev) => {
      const merged: Record<string, string | undefined> = { ...fromQrLocation };
      for (const r of attendanceRows) {
        if (merged[r.patientId] == null && fromApptLocation[r.patientId]) {
          merged[r.patientId] = fromApptLocation[r.patientId];
        }
        if (merged[r.patientId] == null && legacyLocations.length > 0) {
          merged[r.patientId] = legacyLocations[0];
        }
        // Keep unsaved note-form edits when attendance re-hydrates from the Appointment.
        if (merged[r.patientId] == null && prev[r.patientId]?.trim()) {
          merged[r.patientId] = prev[r.patientId];
        }
      }
      return merged;
    });
    for (const r of attendanceRows) {
      if (nextAttendance[r.patientId] == null) {
        nextAttendance[r.patientId] = DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
      }
    }
    setAttendanceByPatient(nextAttendance);
    setSavedAttendanceByPatient({ ...nextAttendance });
  }, [
    attendanceInitKey,
    attendanceRowKey,
    appointment?.id,
    attendanceSignature,
    attendanceRows,
    noteMetadata?.locations,
    practiceLocationOptions,
    patientsById,
    appointment,
    clientLocationLabels,
    existingResponse?.id,
    existingResponse?.item,
  ]);

  // Keep form location state aligned when the appointment modal persists location onto the QR.
  const qrParticipantLocationsKey = useMemo(() => {
    if (!existingResponse?.id) return '';
    return JSON.stringify(getParticipantLocationFromResponseItems(existingResponse.item));
  }, [existingResponse?.id, existingResponse?.item]);

  useEffect(() => {
    if (!qrParticipantLocationsKey || !existingResponse?.id) return;
    const fromQr = getParticipantLocationFromResponseItems(existingResponse.item);
    setLocationByPatient((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [patientId, location] of Object.entries(fromQr)) {
        if (next[patientId] !== location) {
          next[patientId] = location;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [qrParticipantLocationsKey, existingResponse?.id, existingResponse?.item]);

  // Participants-table locations (header / edit modal) must feed the note form + sign checks.
  const externalParticipantLocationsKey = useMemo(
    () => JSON.stringify(externalParticipantLocationByPatientId ?? {}),
    [externalParticipantLocationByPatientId],
  );

  useEffect(() => {
    if (!externalParticipantLocationsKey || externalParticipantLocationsKey === '{}') return;
    const fromExternal = externalParticipantLocationByPatientId ?? {};
    setLocationByPatient((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [patientId, location] of Object.entries(fromExternal)) {
        const trimmed = location?.trim();
        if (!trimmed) continue;
        if (next[patientId] !== trimmed) {
          next[patientId] = trimmed;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [externalParticipantLocationsKey, externalParticipantLocationByPatientId]);

  // Review comments when editing: show and allow resolve; keep in sync with QR
  // Memoized on the serialized extension value so identity survives the
  // unrelated QR updates that happen on every autosave (keeps row memo intact).
  const commentsExtensionJson =
    existingResponse?.extension?.find((e) => e.url === REVIEW_COMMENTS_EXT_URL)?.valueString ?? null;
  const commentsFromQR = useMemo(
    () => (commentsExtensionJson && existingResponse ? getReviewCommentsFromQR(existingResponse) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commentsExtensionJson],
  );
  const [localComments, setLocalComments] = useState<DocumentComment[] | null>(null);
  const displayComments = localComments ?? (commentsFromQR as DocumentComment[]);
  useEffect(() => {
    setLocalComments(null);
  }, [existingResponse?.id]);
  const displayCommentsRef = useRef(displayComments);
  displayCommentsRef.current = displayComments;
  const handleResolveComment = useCallback(
    async (commentId: string) => {
      const existing = existingResponseRef.current;
      if (!existing?.id) return;
      const next = displayCommentsRef.current.map((c) =>
        c.id === commentId ? { ...c, resolvedAt: new Date().toISOString() } : c
      );
      setLocalComments(next);
      const updated = setReviewCommentsOnQR(existing, next);
      await medplum.updateResource(updated);
    },
    [medplum],
  );

  // Comments by item path for inline display under each form section (key matches QRDocumentWithComments)
  const pathCommentsMap = useMemo(() => {
    const map: Record<string, DocumentComment[]> = {};
    displayComments.forEach((c) => {
      const key = c.anchor?.itemPath ?? c.anchor?.linkId;
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return map;
  }, [displayComments]);

  // --- Handlers ---

  const handleTemplateChange = useCallback(
    (newId: string | null) => {
      if (!newId || newId === selectedTemplateId) return;
      userPickedTemplateRef.current = true;
      setSelectedTemplateId(newId);
      setFormKey((k) => k + 1);
      if (getDetailedStatus(existingResponse ?? {}) === 'ai-draft') {
        setManualEditFromAiDraft(true);
      }
      scheduleAutosaveRef.current?.();
    },
    [selectedTemplateId, existingResponse],
  );

  const metadataForSave = useMemo(
    () =>
      noteMetadata && serviceTypeOptions.length > 0
        ? enrichBillingCodesWithCpt(noteMetadata, serviceTypeOptions)
        : noteMetadata,
    [noteMetadata, serviceTypeOptions],
  );

  useEffect(() => {
    if (isOutcomeNote) {
      setInlineFieldErrors({});
      setScrubberResults([]);
      setScrubberMetadataErrors([]);
      setScrubberValidationErrors({});
      return;
    }
    setInlineFieldErrors(
      buildInlineMetadataFieldChecks(
        noteMetadata,
        serviceTypeOptions,
        scrubberParticipantAttendance,
      ),
    );
  }, [isOutcomeNote, noteMetadata, serviceTypeOptions, scrubberParticipantAttendance]);

  const mergedParticipantLocationByPatientId = useMemo((): Record<string, string | undefined> => {
    return {
      ...(existingResponse?.id
        ? getParticipantLocationFromResponseItems(existingResponse.item)
        : {}),
      ...clientLocationLabels,
      ...(externalParticipantLocationByPatientId ?? {}),
      ...locationByPatient,
    };
  }, [
    existingResponse?.id,
    existingResponse?.item,
    clientLocationLabels,
    externalParticipantLocationByPatientId,
    locationByPatient,
  ]);

  const buildCurrentAutosaveSnapshot = useCallback((): NoteAutosaveSnapshot | null => {
    const state = formStateRef.current;
    if (!state || state.loading || !formQuestionnaire) return null;
    const formItems = stripParticipantAttendanceGroup(state.questionnaireResponse.item);
    const existingItemsForSave = existingItemsBaselineRef.current;
    const itemsForSave =
      buildQuestionnaireResponseItemsForSave(formQuestionnaire, formItems, existingItemsForSave) ??
      formItems;
    return {
      templateId: selectedTemplateId,
      responseItems: itemsForSave,
      metadata: metadataForSave,
      attendanceByPatient,
      // Same merge as sign validation — header locations must be in the snapshot or
      // Continue→Sign drain never converges (footer stuck on "Saving...").
      locationByPatient: mergedParticipantLocationByPatientId,
      comments: displayComments,
    };
  }, [
    formQuestionnaire,
    selectedTemplateId,
    metadataForSave,
    attendanceByPatient,
    mergedParticipantLocationByPatientId,
    displayComments,
  ]);

  const runEncounterScrubberCheck = useCallback(
    (
      metadataToSave: NoteMetadataSnapshot,
      requestId?: number,
    ): {
      blocked: boolean;
      showModal: boolean;
      results: EncounterScrubberResult[];
      metadataErrors: MetadataValidationError[];
    } => {
      const scrubberInput = buildEncounterScrubberInput({
        metadata: metadataToSave,
        serviceTypes: serviceTypeOptions,
        participantAttendance: scrubberParticipantAttendance,
      });
      const metaErrors = validateEncounterScrubberMetadata(scrubberInput);

      const applyResults = () => {
        setScrubberMetadataErrors(metaErrors);
        const fieldErrors: Record<string, string> = {};
        for (const err of metaErrors) {
          fieldErrors[err.field] = err.message;
        }
        setScrubberValidationErrors(fieldErrors);

        if (metaErrors.length > 0) {
          setScrubberResults([]);
          return {
            blocked: true,
            showModal: false,
            results: [] as EncounterScrubberResult[],
            metadataErrors: metaErrors,
          };
        }

        const results = scrubEncounterCptCodes(scrubberInput);
        setScrubberResults(results);

        return {
          blocked: false,
          showModal: scrubberResultsHaveWarnings(results),
          results,
          metadataErrors: metaErrors,
        };
      };

      if (requestId != null && requestId !== checkGenerationRef.current) {
        return {
          blocked: metaErrors.length > 0,
          showModal: false,
          results: [],
          metadataErrors: metaErrors,
        };
      }

      return applyResults();
    },
    [serviceTypeOptions, scrubberParticipantAttendance],
  );

  const performPersistSave = useCallback(async (
    metadataOverride?: NoteMetadataSnapshot,
    saveReason: SaveIfChangedReason = 'autosave',
  ): Promise<QuestionnaireResponse | null> => {
    // Re-entrancy is tracked on a ref, not the `saving` state: sign/finish drain saves run
    // back-to-back inside one microtask chain, so a state-based guard would still read the
    // previous save's `true` and report a false "Autosave failed".
    if (!questionnaire || persistSaveInFlightRef.current) return null;

    const state = formStateRef.current;
    if (!state || state.loading) return null;

    const metadataToValidate =
      metadataOverride ??
      (noteMetadata && serviceTypeOptions.length > 0
        ? enrichBillingCodesWithCpt(noteMetadata, serviceTypeOptions)
        : noteMetadata);
    if (metadataToValidate) {
      const preSaveMeta = validateEncounterScrubberMetadata(
        buildEncounterScrubberInput({
          metadata: metadataToValidate,
          serviceTypes: serviceTypeOptions,
          participantAttendance: scrubberParticipantAttendance,
        }),
      );
      if (preSaveMeta.length > 0) {
        const fieldErrors: Record<string, string> = {};
        for (const err of preSaveMeta) {
          fieldErrors[err.field] = err.message;
        }
        setScrubberValidationErrors(fieldErrors);
        setScrubberMetadataErrors(preSaveMeta);
      }
    }

    persistSaveInFlightRef.current = true;
    setSaving(true);
    try {
      const response = state.questionnaireResponse;
      const savedBaseline = existingResponse ?? persistedResponseSnapshotRef.current;

      const isMock = isMockQuestionnaireResponseId(savedBaseline?.id);
      const persistToServer = !isMock;

      if (persistToServer && profile?.resourceType === 'Patient') {
        notify({
          type: 'error',
          title: 'Cannot save note',
          message: 'Clinical notes must be saved with a provider or staff Medplum account.',
        });
        return null;
      }

      if (persistToServer) {
        const subjectRef =
          (isCohortSharedNote && cohortGroupSubjectRef
            ? cohortGroupSubjectRef
            : undefined) ??
          resolvedPatientRef ??
          savedBaseline?.subject?.reference ??
          response.subject?.reference;
        if (!subjectRef) {
          notify({
            type: 'error',
            title: 'Cannot save note',
            message: isCohortSharedNote
              ? 'No Group is linked to this appointment. Reload the page or open the session from the Group.'
              : 'No patient is linked to this appointment. Reload the page or open the appointment from the chart.',
          });
          return null;
        }
      }

      let source = undefined;
      if (profile) {
        source = profile.resourceType === 'Practitioner'
          ? practitionerPreferredReference(profile)
          : createReference(profile);
      }

      let questionnaireRef = questionnaire.url ?? getReferenceString(questionnaire);
      if (persistToServer && !savedBaseline?.id) {
        questionnaireRef = await ensureQuestionnaireOnServer(medplum, questionnaire);
      }

      const hasProviderSignature = savedBaseline?.extension?.some(
        (e) => e.url === PROVIDER_SIGNATURE_EXT_URL,
      );
      const subjectReference =
        (isCohortSharedNote && cohortGroupSubjectRef
          ? cohortGroupSubjectRef
          : undefined) ??
        resolvedPatientRef ??
        savedBaseline?.subject?.reference ??
        response.subject?.reference;

      const formItems = stripParticipantAttendanceGroup(state.questionnaireResponse.item);
      const existingItemsForSave = existingItemsBaselineRef.current;
      const itemsForSave =
        buildQuestionnaireResponseItemsForSave(formQuestionnaire ?? questionnaire, formItems, existingItemsForSave) ??
        formItems;

      if (
        persistToServer &&
        savedBaseline?.id &&
        getDetailedStatus(savedBaseline) === 'ai-draft' &&
        !manualEditFromAiDraft &&
        saveReason === 'autosave'
      ) {
        const serverClinicalJson = JSON.stringify(
          stripParticipantAttendanceGroup(savedBaseline.item),
        );
        const formClinicalJson = JSON.stringify(itemsForSave);
        if (serverClinicalJson.length > formClinicalJson.length + 200) {
          return null;
        }
      }

      const itemWithAttendance = sanitizeResponseItemsForFhir(
        mergeParticipantAttendanceIntoResponseItems(
          itemsForSave,
          attendanceByPatient,
          attendanceRows.map((r) => ({ patientId: r.patientId, displayName: r.displayName })),
          mergedParticipantLocationByPatientId,
        ),
      );

      const updatedExtensions = [
        ...(savedBaseline?.extension?.filter(
          (e) =>
            e.url === PROVIDER_SIGNATURE_EXT_URL ||
            e.url === SUPERVISOR_SIGNATURE_EXT_URL,
        ) ?? []),
        ...((() => {
          const commentsToSave = localComments ?? (savedBaseline ? getReviewCommentsFromQR(savedBaseline) : []);
          if (commentsToSave.length === 0) return savedBaseline?.extension?.filter((e) => e.url === REVIEW_COMMENTS_EXT_URL) ?? [];
          return [{ url: REVIEW_COMMENTS_EXT_URL, valueString: JSON.stringify(commentsToSave) }];
        })()),
        ...(state.questionnaireResponse.extension?.filter(
          (e) =>
            e.url !== PROVIDER_SIGNATURE_EXT_URL &&
            e.url !== SUPERVISOR_SIGNATURE_EXT_URL &&
            e.url !== REVIEW_COMMENTS_EXT_URL &&
            e.url !== FROZEN_TEMPLATE_CANONICAL_EXT &&
            e.url !== FROZEN_TEMPLATE_VERSION_EXT &&
            e.url !== FROZEN_TEMPLATE_DISPLAY_EXT &&
            e.url !== DETAILED_STATUS_EXT_URL,
        ) ?? []),
        ...(savedBaseline?.extension?.filter(
          (e) =>
            e.url === FROZEN_TEMPLATE_CANONICAL_EXT ||
            e.url === FROZEN_TEMPLATE_VERSION_EXT ||
            e.url === FROZEN_TEMPLATE_DISPLAY_EXT,
        ) ?? []),
        ...(!savedBaseline?.id && questionnaire
          ? [
              { url: FROZEN_TEMPLATE_CANONICAL_EXT, valueString: questionnaire.url ?? `Questionnaire/${questionnaire.id}` },
              ...(questionnaire.meta?.versionId
                ? [{ url: FROZEN_TEMPLATE_VERSION_EXT, valueString: questionnaire.meta.versionId }]
                : []),
              ...(questionnaire.title
                ? [{ url: FROZEN_TEMPLATE_DISPLAY_EXT, valueString: questionnaire.title }]
                : []),
            ]
          : []),
        ...((() => {
          const ds = getDetailedStatus(savedBaseline ?? {});
          if (ds === 'ai-draft' && manualEditFromAiDraft) {
            return [{ url: DETAILED_STATUS_EXT_URL, valueCode: 'draft' as const }];
          }
          if (ds === 'generation-failed') {
            return [{ url: DETAILED_STATUS_EXT_URL, valueCode: 'draft' as const }];
          }
          return savedBaseline?.extension?.filter((e) => e.url === DETAILED_STATUS_EXT_URL) ?? [];
        })()),
      ];

      const qrBase: QuestionnaireResponse = {
        ...state.questionnaireResponse,
        item: itemWithAttendance,
        ...(savedBaseline?.id ? { id: savedBaseline.id } : {}),
        resourceType: 'QuestionnaireResponse',
        questionnaire: questionnaireRef,
        subject: subjectReference ? { reference: subjectReference } : undefined,
        encounter: encounterRef
          ? { reference: encounterRef }
          : (savedBaseline?.encounter ?? state.questionnaireResponse.encounter ?? undefined),
        author: profile
          ? profile.resourceType === 'Practitioner'
            ? practitionerPreferredReference(profile)
            : createReference(profile)
          : (savedBaseline?.author ?? state.questionnaireResponse.author ?? undefined),
        source,
        authored: new Date().toISOString(),
        status: hasProviderSignature ? 'completed' : 'in-progress',
        meta: {
          ...(savedBaseline?.meta ?? state.questionnaireResponse.meta),
          tag: [
            ...((savedBaseline?.meta?.tag ?? state.questionnaireResponse.meta?.tag) ?? []).filter(
              (t) => t.system !== APPOINTMENT_TAG_SYSTEM,
            ),
            appointmentTag(appointmentId),
          ],
          ...(episodeOfCare
            ? (() => {
                const accounts = careTeamAccountRefsFromEpisode(episodeOfCare);
                return accounts.length ? { accounts } : {};
              })()
            : {}),
        },
        extension: mergeQuestionnaireResponseExtensionsForSave(savedBaseline, updatedExtensions),
      };

      const metadataBase = metadataOverride ?? noteMetadata;
      const metadataToSave =
        metadataBase && serviceTypeOptions.length > 0
          ? enrichBillingCodesWithCpt(metadataBase, serviceTypeOptions)
          : metadataBase;
      const qrToSave: QuestionnaireResponse = metadataToSave
        ? setNoteMetadataOnQR(qrBase, metadataToSave)
        : qrBase;

      let saved: QuestionnaireResponse;
      if (isMock) {
        saved = qrToSave;
      } else if (savedBaseline?.id) {
        saved = await fhirUpdateQR(medplum, {
          ...qrToSave,
          id: savedBaseline.id,
        });
      } else {
        const toCreate = sanitizeQuestionnaireResponseForCreate(qrToSave);
        try {
          saved = await fhirCreateQR(medplum, toCreate);
        } catch (createErr) {
          const retryWithoutEncounter =
            !!encounterRef &&
            !!toCreate.encounter &&
            createErr instanceof Error &&
            createErr.message.includes('403');
          if (retryWithoutEncounter) {
            const withoutEncounter = sanitizeQuestionnaireResponseForCreate({
              ...qrToSave,
              encounter: undefined,
            });
            saved = await fhirCreateQR(medplum, withoutEncounter);
          } else {
            throw createErr;
          }
        }
      }

      if (
        !isMock &&
        saved.id &&
        metadataToSave &&
        appointment &&
        initialMetadataRef.current !== null
      ) {
        try {
          const syncResult = await syncNoteMetadataEditsToSources({
            medplum,
            before: initialMetadataRef.current,
            after: metadataToSave,
            appointment,
            encounter,
            chargeItems,
          });
          if (syncResult.appointment && onAppointmentUpdated) {
            onAppointmentUpdated(syncResult.appointment);
          }
          initialMetadataRef.current = metadataToSave;
        } catch (syncErr) {
          console.warn('Note metadata source sync failed:', syncErr);
          const syncDetail = getUserFacingErrorMessage(syncErr);
          notify({
            type: 'warning',
            title: 'Metadata saved on note',
            message: `The note snapshot was saved, but some source records could not be updated${syncDetail ? `: ${syncDetail}` : '.'}`,
          });
        }
      }

      if (!isMock && saved.id && appointment?.id) {
        try {
          const savedAppt = await persistNoteFormAttendance(medplum, appointment, attendanceByPatient);
          if (savedAppt !== appointment && onAppointmentUpdated) {
            onAppointmentUpdated(savedAppt);
          }
        } catch (attendanceErr) {
          if (!isSignedNoteLockError(attendanceErr)) {
            console.warn('Note attendance was not copied to the appointment:', attendanceErr);
            const attendanceDetail = getUserFacingErrorMessage(attendanceErr);
            notify({
              type: 'warning',
              title: 'Attendance saved on the note',
              message: `The appointment could not be updated${attendanceDetail ? `: ${attendanceDetail}` : '.'}`,
            });
          }
        }
      }

      return saved;
    } catch (err) {
      console.error('Failed to save note:', err);
      const message = buildNoteSaveErrorMessage({
        base: getUserFacingErrorMessage(err),
        isProduction: config.isProduction,
        is403: isMedplumHttpStatus(err, 403),
      });
      notify({
        type: 'error',
        title: saveReason === 'autosave' ? 'Autosave failed' : "Couldn't save changes",
        message,
      });
      return null;
    } finally {
      persistSaveInFlightRef.current = false;
      setSaving(false);
    }
  }, [
    questionnaire,
    formQuestionnaire,
    profile,
    resolvedPatientRef,
    isCohortSharedNote,
    cohortGroupSubjectRef,
    encounterRef,
    appointmentId,
    existingResponse,
    localComments,
    medplum,
    attendanceByPatient,
    attendanceRows,
    manualEditFromAiDraft,
    noteMetadata,
    serviceTypeOptions,
    encounter,
    chargeItems,
    onAppointmentUpdated,
    appointment,
    mergedParticipantLocationByPatientId,
    scrubberParticipantAttendance,
    episodeOfCare,
  ]);

  const scheduleAutosaveRef = useRef<(() => void) | null>(null);

  const {
    status: autosaveStatus,
    initSnapshot,
    scheduleAutosave,
    saveIfChanged,
    flushDebounced,
    flushAutosaveBeforeLeave,
  } = useProviderNoteAutosave({
    // Keep signed notes from silent autosave; Revise Note persists via Review & Sign (saveIfChanged).
    enabled: Boolean(questionnaire) && !isSigned && !autosavePaused,
    getCurrentSnapshot: buildCurrentAutosaveSnapshot,
    persistSave: async (metadataOverride, reason) => {
      const saved = await performPersistSave(metadataOverride, reason);
      return saved;
    },
    onSaved: (saved, reason) => {
      const wasNew = !persistedResponseSnapshotRef.current?.id && !existingResponse?.id;
      persistedResponseSnapshotRef.current = saved;
      existingItemsBaselineRef.current = stripParticipantAttendanceGroup(saved.item);
      setSavedAttendanceByPatient(getParticipantAttendanceFromResponseItems(saved.item));
      setAutosaveSavedAt(new Date());
      onSave(saved, { isNew: wasNew, reason });
    },
    runPostSaveChecks: (metadata, requestId) => {
      if (!metadata || isOutcomeNote) {
        setScrubberValidationErrors({});
        setScrubberResults([]);
        return;
      }
      runEncounterScrubberCheck(metadata, requestId);
    },
    checkGenerationRef,
  });

  flushDebouncedRef.current = flushDebounced;

  scheduleAutosaveRef.current = scheduleAutosave;

  const [closingEditor, setClosingEditor] = useState(false);
  const [autosaveRetrying, setAutosaveRetrying] = useState(false);
  const leaveInProgressRef = useRef(false);
  const flushBeforeLeaveRef = useRef(flushAutosaveBeforeLeave);
  flushBeforeLeaveRef.current = flushAutosaveBeforeLeave;

  const canAutosaveOnLeave = Boolean(questionnaire) && !isSigned;

  const handleCloseEditor = useCallback(async () => {
    if (editIntent === 'addendum') {
      onCancel();
      return;
    }
    if (leaveInProgressRef.current) return;
    leaveInProgressRef.current = true;
    setClosingEditor(true);
    try {
      await flushBeforeLeaveRef.current();
    } finally {
      leaveInProgressRef.current = false;
      setClosingEditor(false);
    }
    onCancel();
  }, [editIntent, onCancel]);

  const handleAutosaveRetry = useCallback(async () => {
    if (autosaveRetrying || saving) return;
    setAutosaveRetrying(true);
    try {
      flushDebounced();
      await saveIfChanged('autosave');
    } finally {
      setAutosaveRetrying(false);
    }
  }, [autosaveRetrying, saving, flushDebounced, saveIfChanged]);

  const handleCopyNoteText = useCallback(async () => {
    if (!questionnaire) {
      throw new Error('No questionnaire');
    }
    const state = formStateRef.current;
    if (!state || state.loading) {
      throw new Error('Form not ready');
    }
    const response = {
      resourceType: 'QuestionnaireResponse' as const,
      status: 'in-progress' as const,
      item: stripParticipantAttendanceGroup(state.questionnaireResponse.item),
    };
    const text = formatNoteTextForCopy(questionnaire, response);
    if (!text.trim()) {
      throw new Error('No note text');
    }
    await navigator.clipboard.writeText(text);
  }, [questionnaire]);

  useEffect(() => {
    if (!canAutosaveOnLeave) return;

    const onPageHide = () => {
      void flushBeforeLeaveRef.current();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      void flushBeforeLeaveRef.current();
    };
  }, [canAutosaveOnLeave]);

  const lastReportedAutosaveStatusRef = useRef<AutosaveStatus>('saved');
  useEffect(() => {
    if (autosaveStatus === lastReportedAutosaveStatusRef.current) return;
    lastReportedAutosaveStatusRef.current = autosaveStatus;
    onAutosaveStatusChange?.(autosaveStatus);
  }, [autosaveStatus, onAutosaveStatusChange]);

  const establishAutosaveBaseline = useCallback(() => {
    const snapshot = buildCurrentAutosaveSnapshot();
    if (snapshot) {
      initSnapshot(snapshot);
    }
  }, [buildCurrentAutosaveSnapshot, initSnapshot]);

  const handleNoteMetadataChange = useCallback(
    (next: NoteMetadataSnapshot) => {
      const prevBilling = noteMetadata?.billingCodes;
      const nextBilling = next.billingCodes;
      const billingChanged = JSON.stringify(prevBilling) !== JSON.stringify(nextBilling);
      if (billingChanged) {
        userEditedBillingRef.current = true;
      }

      setNoteMetadata(next);

      if (
        billingChanged
        && !userPickedTemplateRef.current
        && !existingResponse?.id
        && !isSigned
      ) {
        const prevHs = prevBilling?.[0]?.healthcareServiceId;
        const nextHs = nextBilling?.[0]?.healthcareServiceId;
        if (nextHs && nextHs !== prevHs) {
          const sessionType =
            serviceTypeOptions.find((st) => st.id === nextHs)
            ?? allSessionTypes.find((st) => st.id === nextHs);
          const noteTemplateId = sessionType?.noteTemplateId?.trim();
          if (noteTemplateId) {
            const match =
              effectiveTemplates.find((t) => t.id === noteTemplateId)
              ?? allAppointmentNoteTemplates.find((t) => t.id === noteTemplateId);
            if (match && match.id !== selectedTemplateId) {
              setSelectedTemplateId(match.id);
              stableDefaultResponseRef.current = undefined;
              setFormKey((k) => k + 1);
            } else if (!match) {
              // Selected service's default is incompatible / unknown — clear rather than keep stale.
              const currentStillOk =
                selectedTemplateId
                && (effectiveTemplates.some((t) => t.id === selectedTemplateId)
                  || allAppointmentNoteTemplates.some((t) => t.id === selectedTemplateId));
              if (!currentStillOk) {
                setSelectedTemplateId(null);
                stableDefaultResponseRef.current = undefined;
                setFormKey((k) => k + 1);
              }
            }
          }
        }
      }

      scheduleAutosaveRef.current?.();
    },
    [
      noteMetadata?.billingCodes,
      existingResponse?.id,
      isSigned,
      serviceTypeOptions,
      allSessionTypes,
      effectiveTemplates,
      allAppointmentNoteTemplates,
      selectedTemplateId,
    ],
  );

  const handleParticipantAttendanceChange = useCallback(
    (patientId: string, status: ParticipantAttendanceFhirStatus) => {
      setAttendanceByPatient((prev) => ({ ...prev, [patientId]: status }));
      if (locationRequiredForParticipantAttendance(status)) {
        setLocationByPatient((prev) => {
          if (prev[patientId]?.trim()) return prev;
          const fromApptLocation = clientLocationLabels;
          const filled = resolveDefaultParticipantLocation({
            confirmationLabel: fromApptLocation[patientId],
            practiceLocations: practiceLocationOptions,
            patient: patientsById[patientId],
            legacyMetadataLocations: noteMetadata?.locations,
          });
          if (!filled) return prev;
          return { ...prev, [patientId]: filled };
        });
      } else {
        setParticipantLocationErrors((prev) => {
          if (!prev[patientId]) return prev;
          const next = { ...prev };
          delete next[patientId];
          return next;
        });
      }
      scheduleAutosaveRef.current?.();
    },
    [clientLocationLabels, practiceLocationOptions, patientsById, noteMetadata?.locations],
  );

  const handleParticipantLocationChange = useCallback(
    (patientId: string, location: string | undefined) => {
      setLocationByPatient((prev) => ({ ...prev, [patientId]: location }));
      setParticipantLocationErrors((prev) => {
        if (!prev[patientId]) return prev;
        const next = { ...prev };
        delete next[patientId];
        return next;
      });
      scheduleAutosaveRef.current?.();
    },
    [],
  );

  const validateRequiredFields = useCallback((): boolean => {
    if (!formQuestionnaire) return false;
    const state = formStateRef.current;
    if (!state || state.loading) return false;
    const missingRequired = getMissingRequiredFields(formQuestionnaire, state.questionnaireResponse);
    if (missingRequired.length > 0) {
      scrollToAndFlashQuestionnaireItem(missingRequired[0].itemPath);
      notify({
        type: 'error',
        title: 'Missing required fields',
        message: `Please complete all required fields before signing (${missingRequired.length} remaining).`,
      });
      return false;
    }

    if (attendanceRows.length > 0) {
      const appointmentAttendance = appointment
        ? getExplicitParticipantAttendanceFromAppointment(appointment)
        : {};
      const effectiveAttendanceByPatient: Record<string, ParticipantAttendanceFhirStatus | undefined> = {
        ...attendanceByPatient,
        ...appointmentAttendance,
      };
      const effectiveLocationByPatient = { ...mergedParticipantLocationByPatientId };
      const fromApptLocation = clientLocationLabels;
      const legacyLocations = nonUrlLocationsFromMetadata(noteMetadata?.locations);
      let locationsChanged = false;
      for (const row of attendanceRows) {
        const status = effectiveAttendanceByPatient[row.patientId];
        if (!status || !locationRequiredForParticipantAttendance(status)) continue;
        if (effectiveLocationByPatient[row.patientId]?.trim()) continue;
        const filled = resolveDefaultParticipantLocation({
          confirmationLabel: fromApptLocation[row.patientId],
          practiceLocations: practiceLocationOptions,
          patient: patientsById[row.patientId],
          legacyMetadataLocations: legacyLocations,
        });
        if (filled) {
          effectiveLocationByPatient[row.patientId] = filled;
          locationsChanged = true;
        }
      }
      if (locationsChanged) {
        setLocationByPatient((prev) => ({ ...prev, ...effectiveLocationByPatient }));
      }

      const unresolved = attendanceRows.filter((row) => {
        const status = normalizeCalendarClientAttendanceStatus(
          effectiveAttendanceByPatient[row.patientId],
        );
        return !status || !isCalendarClientAttendanceResolved(status);
      });
      if (unresolved.length > 0) {
        const first = unresolved[0];
        scrollToNoteMetadataField(`participant-attendance-${first.patientId}`);
        const names = unresolved.map((row) => row.displayName).join(', ');
        notify({
          type: 'error',
          title: 'Attendance incomplete',
          message: `Set attendance (Attended, No show, or Cancelled) for ${names} before signing. Scheduled is not a final attendance.`,
        });
        return false;
      }

      const participantValidation = validateParticipantAttendance(
        attendanceRows,
        effectiveAttendanceByPatient,
        effectiveLocationByPatient,
      );
      if (!participantValidation.success) {
        setParticipantLocationErrors(participantValidation.locationErrorsByPatientId);
        const missingLocationRows = attendanceRows.filter(
          (row) => participantValidation.locationErrorsByPatientId[row.patientId],
        );
        const firstMissingPatientId = missingLocationRows[0]?.patientId;
        if (firstMissingPatientId) {
          scrollToNoteMetadataField(`participant-location-${firstMissingPatientId}`);
        }
        const missingNames = missingLocationRows.map((row) => row.displayName).filter(Boolean);
        notify({
          type: 'error',
          title: missingLocationRows.length > 0
            ? 'Participant location required'
            : 'Participant attendance invalid',
          message:
            missingLocationRows.length > 0
              ? `Enter a location for each attended participant before signing${
                  missingNames.length ? ` (${missingNames.join(', ')})` : ''
                }. Cancelled and no-show participants do not need a location.`
              : 'Fix participant attendance before signing.',
        });
        return false;
      }
      setParticipantLocationErrors({});
    }

    return true;
  }, [
    formQuestionnaire,
    attendanceRows,
    attendanceByPatient,
    mergedParticipantLocationByPatientId,
    appointment,
    clientLocationLabels,
    practiceLocationOptions,
    patientsById,
    noteMetadata?.locations,
  ]);

  const collectReviewChecks = useCallback(
    async (
      metadataToSavePreview: NoteMetadataSnapshot | null | undefined,
      afterConfirm: 'sign' | 'finish',
      savedResponseId?: string,
    ): Promise<void> => {
      const state = formStateRef.current;
      const responseItems =
        state && !('loading' in state && state.loading) && 'questionnaireResponse' in state
          ? state.questionnaireResponse?.item
          : undefined;

      const draft =
        formQuestionnaire && !isOutcomeNote
          ? buildLocalDraftChecks({ questionnaire: formQuestionnaire, responseItems })
          : [];
      setReviewDraftChecks(draft);

      let automatic: AiNoteCheckResult[] = [];
      let autoError: string | null = null;
      const qrId = savedResponseId ?? existingResponse?.id;
      if (qrId && !isMockQuestionnaireResponseId(qrId) && !isOutcomeNote) {
        try {
          const available = await isAiNoteCheckBotAvailable(medplum);
          if (available) {
            const checked = await requestAiNoteCheck(medplum, qrId, {
              force: false,
              reason: 'manual',
            });
            automatic = getAiNoteCheckResultsFromQR(checked)?.checks ?? [];
          }
        } catch (err) {
          if (!isAiNoteCheckBotUnavailableError(err)) {
            autoError = getUserFacingErrorMessage(err, 'Automatic note checks could not be completed.');
          }
        }
      }
      setReviewAutomaticChecks(automatic);
      setReviewChecksError(autoError);

      if (!metadataToSavePreview || isOutcomeNote) {
        pendingReviewRef.current = {
          metadata: metadataToSavePreview ?? (metadataForSave as NoteMetadataSnapshot),
          reviewableResults: [],
          afterConfirm,
        };
        setScrubberResults([]);
        setScrubberMetadataErrors([]);
        setReviewBlocked(false);
        setReviewBlockedMessage(undefined);
        return;
      }

      const scrubberCheck = runEncounterScrubberCheck(metadataToSavePreview);
      if (scrubberCheck.blocked) {
        setReviewBlocked(true);
        setReviewBlockedMessage(
          'Complete the required billing evaluation fields before signing.',
        );
        pendingReviewRef.current = {
          metadata: metadataToSavePreview,
          reviewableResults: [],
          afterConfirm,
        };
        return;
      }

      const alreadyReviewed = Boolean(metadataToSavePreview.encounterScrubberReview?.reviewedAt);
      const reviewableResults =
        scrubberCheck.showModal && !alreadyReviewed
          ? scrubberResultsNeedUserReview(scrubberCheck.results)
          : [];

      setReviewBlocked(false);
      setReviewBlockedMessage(undefined);
      pendingReviewRef.current = {
        metadata: metadataToSavePreview,
        reviewableResults,
        afterConfirm,
      };
      setScrubberResults(reviewableResults);
      setScrubberMetadataErrors(scrubberCheck.metadataErrors);
    },
    [
      formQuestionnaire,
      isOutcomeNote,
      existingResponse?.id,
      medplum,
      metadataForSave,
      runEncounterScrubberCheck,
    ],
  );

  const handleReviewAndSign = useCallback(async () => {
    if (!questionnaire || saving || signing || !onRequestSign) return;
    if (!validateRequiredFields()) return;

    setSigning(true);
    setReviewContinueAction('sign');
    setReviewModalOpen(true);
    setReviewChecksLoading(true);
    setReviewChecksError(null);
    try {
      flushDebounced();
      const metadataToSavePreview = metadataForSave
        ? { ...metadataForSave, encounterScrubberReview: undefined }
        : undefined;
      const saveResult = await saveIfChanged('sign', metadataToSavePreview);
      if (!saveResult.skipped && !saveResult.saved) {
        setReviewModalOpen(false);
        return;
      }

      await collectReviewChecks(
        metadataToSavePreview ?? metadataForSave,
        'sign',
        saveResult.response?.id ?? existingResponse?.id,
      );
    } finally {
      setReviewChecksLoading(false);
      setSigning(false);
    }
  }, [
    questionnaire,
    saving,
    signing,
    onRequestSign,
    validateRequiredFields,
    flushDebounced,
    metadataForSave,
    saveIfChanged,
    collectReviewChecks,
    existingResponse?.id,
  ]);

  const handleReviewModalContinue = useCallback(
    async (decisions: Record<string, ScrubberSuggestionDecision>) => {
      const pending = pendingReviewRef.current;
      if (!pending) return;

      let updatedMetadata = pending.metadata;
      if (pending.reviewableResults.length > 0) {
        updatedMetadata = applyAcceptedScrubberResults(
          pending.metadata,
          pending.reviewableResults,
          decisions,
          serviceTypeOptions,
        );
        updatedMetadata = {
          ...updatedMetadata,
          encounterScrubberReview: buildEncounterScrubberReview(
            pending.reviewableResults,
            decisions,
          ),
        };
        setNoteMetadata(updatedMetadata);
        runEncounterScrubberCheck(updatedMetadata);
      }

      const afterConfirm = pending.afterConfirm;
      pendingReviewRef.current = null;
      setReviewModalOpen(false);

      const saveResult = await saveIfChanged(afterConfirm, updatedMetadata);
      if (!saveResult.saved && !saveResult.skipped) {
        return;
      }

      if (afterConfirm === 'sign') {
        // Revise Note: quality checks → revision reason → signature.
        if (editIntent === 'addendum' && onBeforeResign) {
          const ok = await onBeforeResign(saveResult.response);
          if (!ok) return;
        } else {
          const resolveBeforeSign = onBeforeSign ?? onBeforeSave;
          if (resolveBeforeSign) {
            const ok = await resolveBeforeSign();
            if (!ok) return;
          }
        }
        onRequestSign?.();
      } else if (afterConfirm === 'finish') {
        onFinishEdit?.();
      }
    },
    [
      serviceTypeOptions,
      saveIfChanged,
      onFinishEdit,
      runEncounterScrubberCheck,
      onRequestSign,
      editIntent,
      onBeforeResign,
      onBeforeSign,
      onBeforeSave,
    ],
  );

  const handleReviewModalCancel = useCallback(() => {
    if (saving || reviewChecksLoading) return;
    pendingReviewRef.current = null;
    setReviewModalOpen(false);
  }, [saving, reviewChecksLoading]);

  const handleReviewFindingNavigate = useCallback((itemPath: string) => {
    pendingReviewRef.current = null;
    setReviewModalOpen(false);
    // The modal keeps body scroll locked until its close transition finishes.
    window.setTimeout(() => scrollToNoteCheckTarget(itemPath), REVIEW_MODAL_CLOSE_MS);
  }, []);

  const primaryActionLoading = saving || signing || closingEditor;
  // Draft: sign when unsigned. Addendum revise: always re-sign after quality checks + reason.
  const showSignAction =
    Boolean(onRequestSign)
    && (editIntent === 'addendum' || (editIntent === 'draft' && !isSigned));
  const showFinishAction = false;
  const showEditorFooter = !isSigned || editIntent === 'addendum';

  const handleFormChange = useCallback(() => {
    if (getDetailedStatus(existingResponseRef.current ?? persistedResponseSnapshotRef.current ?? {}) === 'ai-draft') {
      setManualEditFromAiDraft(true);
    }
    scheduleAutosaveRef.current?.();
  }, []);

  useEffect(() => {
    setManualEditFromAiDraft(false);
  }, [existingResponse?.id]);

  const resolvedSessionModality = useMemo(
    () => noteMetadata?.modality ?? (appointment ? resolveAppointmentModality(appointment) : undefined),
    [noteMetadata?.modality, appointment],
  );

  const formQuestionnaireHasModalityGate = useMemo(
    () => questionnaireHasSessionModalityGate(formQuestionnaire),
    [formQuestionnaire],
  );

  const defaultResponse = useMemo(() => {
    if (!formQuestionnaire) return undefined;
    if (formQuestionnaireHasModalityGate && !resolvedSessionModality) return undefined;
    const stripped = existingResponse?.id
      ? stripParticipantAttendanceGroup(existingResponse.item)
      : [];
    const withModality = upsertSessionModalityAnswer(stripped, resolvedSessionModality);
    const shaped = shapeResponseItemsForQuestionnaire(withModality, formQuestionnaire) ?? withModality;
    if (existingResponse?.id) {
      return { ...existingResponse, item: shaped };
    }
    return {
      resourceType: 'QuestionnaireResponse' as const,
      status: 'in-progress' as const,
      questionnaire: formQuestionnaire.url,
      item: shaped,
    };
  }, [existingResponse, formQuestionnaire, resolvedSessionModality, formQuestionnaireHasModalityGate]);

  const isDefaultResponseReady = isProgressNoteDefaultResponseReady(defaultResponse, {
    requiresModalityGate: formQuestionnaireHasModalityGate,
    resolvedModality: resolvedSessionModality,
  });

  if (isDefaultResponseReady && defaultResponse) {
    stableDefaultResponseRef.current = defaultResponse;
  }

  const stableDefaultResponse = isDefaultResponseReady ? stableDefaultResponseRef.current : undefined;

  const questionnaireStableKey =
    formQuestionnaire?.id ?? formQuestionnaire?.url ?? selectedTemplateId ?? 'no-q';

  const awaitingQuestionnaireForEdit =
    Boolean(existingResponse?.id) &&
    !useMockTemplateLibrary &&
    !questionnaire &&
    (appointmentResponsesLoading || clientQuestionnaireLoading);

  const templateLoadFailed =
    Boolean(existingResponse?.id) &&
    !useMockTemplateLibrary &&
    !questionnaire &&
    !appointmentResponsesLoading &&
    !clientQuestionnaireLoading;

  // --- Render ---
  return (
    <Card withBorder padding={0}>
      <Stack gap="md" p="lg">
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Group gap="sm" align="center" wrap="wrap">
            <Title order={3}>Progress Note</Title>
            {headerBadge}
            {cohortNoteKind ? (
              <CohortProgressNoteTitleExtras
                noteKind={cohortNoteKind}
                sharedNoteHref={sharedNoteHref}
              />
            ) : null}
          </Group>
          {headerActions}
        </Group>

        {editorLocked ? (
          <Alert color="violet" variant="light" icon={<Loader size="xs" />}>
            AI is generating a draft note. Editing is disabled until generation completes.
          </Alert>
        ) : null}

        {noteMetadata && appointment && (
          <SessionMetadataWorkspace
            appointment={appointment}
            metadata={noteMetadata}
            onChange={handleNoteMetadataChange}
            serviceTypeOptions={serviceTypeOptions}
            participantAttendanceRows={attendanceRows}
            participantAttendanceByPatientId={attendanceByPatient}
            savedParticipantAttendanceByPatientId={savedAttendanceByPatient}
            participantLocationByPatientId={mergedParticipantLocationByPatientId}
            participantLocationOptionsByPatientId={participantLocationOptionsByPatientId}
            participantLocationErrorsByPatientId={participantLocationErrors}
            onParticipantAttendanceChange={handleParticipantAttendanceChange}
            onParticipantLocationChange={handleParticipantLocationChange}
            requireResolvedAttendance={isCohortSharedNote}
            sessionTiming={sessionTiming}
            scrubberValidationErrors={scrubberValidationErrors}
            inlineFieldWarnings={inlineFieldErrors}
            readOnly={editorLocked}
            onEditAppointment={onEditAppointment}
          />
        )}

        {outcomeTemplateSuggestion ? (
          <Alert color="blue" variant="light" title="Switch note template?">
            <Stack gap="xs">
              <Text size="sm">
                This appointment is marked as{' '}
                {outcomeTemplateSuggestion.kind === 'late-cancel' ? 'late cancelled' : 'no show'}. Switch to the
                matching note template? Your current answers will be cleared.
              </Text>
              <Group gap="xs">
                <Button size="xs" variant="light" onClick={applyOutcomeTemplateSuggestion}>
                  Switch template
                </Button>
                <Button size="xs" variant="subtle" onClick={() => setOutcomeTemplateSuggestion(null)}>
                  Keep current template
                </Button>
              </Group>
            </Stack>
          </Alert>
        ) : null}

        {/* Template selector */}
        <Select
          data={templateSelectData}
          value={selectedTemplateId}
          onChange={handleTemplateChange}
          placeholder={
            useMockTemplateLibrary
              ? templateSelectData.length === 0
                ? 'No mock templates'
                : 'Select a template…'
              : templatesLoading
                ? 'Loading templates…'
                : templateSelectData.length === 0
                  ? 'No templates in Template Library'
                  : 'Select a template…'
          }
          disabled={
            editorLocked ||
            (useMockTemplateLibrary ? templateSelectData.length === 0 : templatesLoading || templateSelectData.length === 0)
          }
          style={{ maxWidth: 420 }}
        />

        <Divider />

        {/* Dynamic form body — wait for Questionnaire resolution so useQuestionnaireForm can hydrate */}
        {templateLoadFailed ? (
          <Alert color="red" title="Could not load note template">
            The questionnaire for this note could not be loaded from the server (check access policies or
            that the template still exists). Cancel and reopen the appointment, or contact an administrator.
          </Alert>
        ) : awaitingQuestionnaireForEdit ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            Loading note template…
          </Text>
        ) : formQuestionnaire && stableDefaultResponse ? (
          <NoteFormBody
            key={`note-form-${formInstanceKeyRef.current}-${questionnaireStableKey}-mod-${resolvedSessionModality ?? 'pending'}-k${formKey}-${cohortPatientSectionKey}`}
            questionnaire={formQuestionnaire}
            defaultResponse={stableDefaultResponse}
            formStateRef={formStateRef}
            commentsByPath={pathCommentsMap}
            onResolveComment={handleResolveComment}
            canResolve={!isSigned}
            readOnly={editorLocked}
            onFormChange={handleFormChange}
            onFormReady={establishAutosaveBaseline}
            onRegisterTextUndoCapture={registerTextUndoCapture}
          />
        ) : formQuestionnaire ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            Loading session context…
          </Text>
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {useMockTemplateLibrary
              ? 'Select a template to start writing your note.'
              : templatesLoading
                ? 'Loading templates…'
                : 'Select a template to start writing your note.'}
          </Text>
        )}

        <NoteReviewAndSignModal
          opened={reviewModalOpen}
          onCancel={handleReviewModalCancel}
          onContinue={(decisions) => void handleReviewModalContinue(decisions)}
          continueAction={reviewContinueAction}
          loading={saving}
          checksLoading={reviewChecksLoading}
          checksError={reviewChecksError}
          draftChecks={reviewDraftChecks}
          automaticChecks={reviewAutomaticChecks}
          scrubberResults={scrubberResults}
          metadataErrors={scrubberMetadataErrors}
          blocked={reviewBlocked}
          blockedMessage={reviewBlockedMessage}
          noteMetadata={pendingReviewRef.current?.metadata ?? noteMetadata}
          serviceTypes={serviceTypeOptions}
          onRecommendationApplied={(metadata) => {
            setNoteMetadata(metadata);
          }}
          onNavigateToFinding={handleReviewFindingNavigate}
        />
      </Stack>

      {showEditorFooter ? (
        <>
          {!isSigned && autosaveStatus === 'error' ? (
            <NoteAutosaveRecoveryBanner
              onRetry={() => void handleAutosaveRetry()}
              onCopyNote={handleCopyNoteText}
              retrying={autosaveRetrying || saving}
            />
          ) : null}
          <ProgressNoteStickyFooter
            variant={editIntent === 'addendum' ? 'addendum' : 'draft'}
            autosaveStatus={autosaveStatus}
            autosaveSavedAt={autosaveSavedAt}
            onCancel={editIntent === 'addendum' ? () => void handleCloseEditor() : undefined}
            cancelLabel="Cancel"
            onReviewAndSign={showSignAction ? () => void handleReviewAndSign() : undefined}
            onReviewAndClose={undefined}
            showSignAction={showSignAction}
            showFinishAction={showFinishAction}
            loading={primaryActionLoading}
            closingEditor={closingEditor}
            disabled={!questionnaire || editorLocked}
          />
        </>
      ) : null}
    </Card>
  );
}
